import React from "react";
import { useTranslation } from "react-i18next";

import { PREVIEW_MODE_NO_ORDER } from "../shared/constants";
import {
    PreviewSectionTitle,
    OrderSectionContainer,
    OrderItem,
    OrderItemInfo,
    OrderItemImage,
    OrderItemDetails,
    OrderItemName,
    OrderItemQty,
    OrderItemPrice,
    OrderTotal,
    TotalLabel,
    TotalAmount,
    StepperContainer,
    StepperStep,
    StepperDot,
    StepperLabel,
    StepperLine,
    OrderInfoCard,
    OrderInfoLeft,
    OrderInfoLabel,
    OrderInfoValue,
    StatusBadge,
    NoOrderContainer,
    NoOrderIcon,
    NoOrderText,
    NoOrderSubtext,
} from "./styled";

type OrderSectionProps = {
    orderEnabled: boolean;
    orderTitleVisible: boolean;
    orderSectionTitle: string;
    orderIconPath: string;
    orderCustomIcon: string;
    previewMode: "with_order" | "no_order";
    showStepper: boolean;
    showOrderDetails: boolean;
    orderStatuses: Array<{ key: string; label: string; active: boolean }>;
    activeStepIndex: number;
    mainColor: string;
    primaryColor: string;
};

export function OrderSection({
    orderEnabled,
    orderTitleVisible,
    orderSectionTitle,
    orderIconPath,
    orderCustomIcon,
    previewMode,
    showStepper,
    showOrderDetails,
    orderStatuses,
    activeStepIndex,
    mainColor,
    primaryColor,
}: OrderSectionProps): JSX.Element | null {
    const { t } = useTranslation();

    if (!orderEnabled) return null;

    return (
        <OrderSectionContainer>
            {orderTitleVisible && (
                <PreviewSectionTitle>
                    {orderCustomIcon ? (
                        <img
                            src={orderCustomIcon}
                            alt=""
                            style={{
                                width: 14,
                                height: 14,
                                objectFit: "contain",
                            }}
                        />
                    ) : (
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill={primaryColor}
                        >
                            <path d={orderIconPath} />
                        </svg>
                    )}
                    {orderSectionTitle}
                </PreviewSectionTitle>
            )}

            {previewMode === PREVIEW_MODE_NO_ORDER ? (
                <NoOrderContainer>
                    <NoOrderIcon>
                        <svg viewBox="0 0 24 24">
                            <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                    </NoOrderIcon>
                    <NoOrderText>{t("No order")}</NoOrderText>
                    <NoOrderSubtext>
                        {t("Scan a table or order QR code to see your order")}
                    </NoOrderSubtext>
                </NoOrderContainer>
            ) : (
                <>
                    {showStepper && (
                        <StepperContainer>
                            {orderStatuses.map((status, idx) => (
                                <React.Fragment key={status.key}>
                                    {idx > 0 && (
                                        <StepperLine
                                            isCompleted={idx <= activeStepIndex}
                                            mainColor={primaryColor}
                                            style={{
                                                position: "relative",
                                                top: "auto",
                                                left: "auto",
                                                flex: 1,
                                                margin: "0 -4px",
                                            }}
                                        />
                                    )}
                                    <StepperStep
                                        isActive={idx === activeStepIndex}
                                        isCompleted={idx <= activeStepIndex}
                                        mainColor={primaryColor}
                                    >
                                        <StepperDot
                                            isActive={idx === activeStepIndex}
                                            isCompleted={idx <= activeStepIndex}
                                            mainColor={primaryColor}
                                        >
                                            {idx < activeStepIndex && (
                                                <svg viewBox="0 0 24 24">
                                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                                </svg>
                                            )}
                                        </StepperDot>
                                        <StepperLabel
                                            isActive={idx === activeStepIndex}
                                            isCompleted={idx <= activeStepIndex}
                                            mainColor={primaryColor}
                                        >
                                            {status.label}
                                        </StepperLabel>
                                    </StepperStep>
                                </React.Fragment>
                            ))}
                        </StepperContainer>
                    )}

                    {showOrderDetails && (
                        <OrderInfoCard
                            mainColor={mainColor}
                            primaryColor={primaryColor}
                        >
                            <OrderInfoLeft>
                                <OrderInfoLabel>
                                    {t("Order number")}
                                </OrderInfoLabel>
                                <OrderInfoValue>#1247</OrderInfoValue>
                            </OrderInfoLeft>
                            <OrderInfoLeft>
                                <OrderInfoLabel>{t("Time")}</OrderInfoLabel>
                                <OrderInfoValue>14:32</OrderInfoValue>
                            </OrderInfoLeft>
                            <StatusBadge
                                mainColor={mainColor}
                                primaryColor={primaryColor}
                            >
                                {orderStatuses[activeStepIndex]?.label ||
                                    t("In preparation")}
                            </StatusBadge>
                        </OrderInfoCard>
                    )}

                    <OrderItem>
                        <OrderItemInfo>
                            <OrderItemImage>🍔</OrderItemImage>
                            <OrderItemDetails>
                                <OrderItemName>Burger Classic</OrderItemName>
                                <OrderItemQty>x2</OrderItemQty>
                            </OrderItemDetails>
                        </OrderItemInfo>
                        <OrderItemPrice mainColor={primaryColor}>
                            25,00 €
                        </OrderItemPrice>
                    </OrderItem>
                    <OrderItem>
                        <OrderItemInfo>
                            <OrderItemImage>🥗</OrderItemImage>
                            <OrderItemDetails>
                                <OrderItemName>Salade Caesar</OrderItemName>
                                <OrderItemQty>x1</OrderItemQty>
                            </OrderItemDetails>
                        </OrderItemInfo>
                        <OrderItemPrice mainColor={primaryColor}>
                            14,50 €
                        </OrderItemPrice>
                    </OrderItem>
                    <OrderItem>
                        <OrderItemInfo>
                            <OrderItemImage>🍟</OrderItemImage>
                            <OrderItemDetails>
                                <OrderItemName>Frites maison</OrderItemName>
                                <OrderItemQty>x2</OrderItemQty>
                            </OrderItemDetails>
                        </OrderItemInfo>
                        <OrderItemPrice mainColor={primaryColor}>
                            9,00 €
                        </OrderItemPrice>
                    </OrderItem>
                    <OrderItem>
                        <OrderItemInfo>
                            <OrderItemImage>🥤</OrderItemImage>
                            <OrderItemDetails>
                                <OrderItemName>Coca-Cola</OrderItemName>
                                <OrderItemQty>x3</OrderItemQty>
                            </OrderItemDetails>
                        </OrderItemInfo>
                        <OrderItemPrice mainColor={primaryColor}>
                            10,50 €
                        </OrderItemPrice>
                    </OrderItem>
                    <OrderTotal mainColor={primaryColor}>
                        <TotalLabel>Total</TotalLabel>
                        <TotalAmount mainColor={primaryColor}>
                            59,00 €
                        </TotalAmount>
                    </OrderTotal>
                </>
            )}
        </OrderSectionContainer>
    );
}
